import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, Alert,
  IconButton, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Sync as SyncIcon,
  LinkOff as DisconnectIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { calendarSourcesApi, calendarConnectApi } from '../api/services';
import type { CalendarSource } from '../types/models';

// Microsoft icon SVG inline
function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Google icon SVG inline
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function ProviderIcon({ sourceType }: { sourceType?: string }) {
  if (sourceType === 'Microsoft365') return <MicrosoftIcon />;
  if (sourceType === 'Google') return <GoogleIcon />;
  return <SyncIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
}

export default function ConnectCalendarCard() {
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [isPersonal, setIsPersonal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const all = await calendarSourcesApi.getAll();
      setSources(all.filter(s => s.sourceType === 'Microsoft365' || s.sourceType === 'Google'));
    } catch (err) {
      console.error('Failed to load sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleConnect = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    setError(null);
    try {
      const name = provider === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      const connectData = { name, isPersonal, eaVisibility: isPersonal ? 'TimeOnly' : undefined, returnPath: '/calendar' };
      const data = provider === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft(connectData)
        : await calendarConnectApi.connectGoogle(connectData);
      if (data.authUrl) {
        // Navigate in same window — callback will redirect back to /calendar?connected=...
        sessionStorage.setItem('calendar-oauth-return', '/calendar');
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start connection';
      setError(typeof msg === 'string' ? msg : 'Failed to start connection');
      setConnecting(null);
    }
  };

  const handleSync = async (sourceId: string) => {
    setSyncingId(sourceId);
    setError(null);
    try {
      await calendarConnectApi.sync(sourceId);
      loadSources();
    } catch (err: any) {
      const data = err.response?.data;
      const msg = data?.error || data?.message || 'Sync failed';
      setError(typeof msg === 'string' ? msg : 'Sync failed');
    } finally {
      setSyncingId(null);
    }
  };

  const handleDisconnect = async (sourceId: string) => {
    if (!window.confirm('Disconnect this calendar? All synced events from this source will be removed.')) return;
    setDisconnectingId(sourceId);
    try {
      await calendarConnectApi.disconnect(sourceId);
      loadSources();
    } catch (err) {
      console.error('Failed to disconnect', err);
    } finally {
      setDisconnectingId(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Connect Calendar</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<MicrosoftIcon />}
            onClick={() => handleConnect('microsoft')}
            disabled={connecting !== null}
            sx={{
              bgcolor: '#0078D4',
              '&:hover': { bgcolor: '#106EBE' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              whiteSpace: 'nowrap',
            }}
          >
            {connecting === 'microsoft' ? 'Connecting...' : 'Connect Microsoft 365'}
          </Button>
          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={() => handleConnect('google')}
            disabled={connecting !== null}
            sx={{
              bgcolor: '#1A1A1A',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: '#2D2D2D' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              whiteSpace: 'nowrap',
            }}
          >
            {connecting === 'google' ? 'Connecting...' : 'Connect Google Calendar'}
          </Button>
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={isPersonal}
              onChange={e => setIsPersonal(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              This is a personal calendar (EA will see time blocks only, not event details)
            </Typography>
          }
        />

        {/* Connected calendars list */}
        {sources.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Connected Calendars</Typography>
            <Stack spacing={1}>
              {sources.map((source) => (
                <Box
                  key={source.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <ProviderIcon sourceType={source.sourceType} />
                    <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{source.name}</Typography>
                    <Chip
                      label={source.needsReconnect ? 'Disconnected' : source.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={source.needsReconnect ? 'warning' : source.isActive ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ height: 22 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                    {source.providerEmail && (
                      <Typography variant="caption" color="text.secondary">{source.providerEmail}</Typography>
                    )}
                    {source.needsReconnect ? (
                      <Chip label="Needs reconnect" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {source.lastSyncedAt
                          ? `Last synced ${format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')}`
                          : 'Never synced'}
                      </Typography>
                    )}
                    {source.isPersonal && (
                      <Chip label="Personal" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton
                      size="small"
                      onClick={() => handleSync(source.id)}
                      disabled={syncingId === source.id || source.needsReconnect}
                      title={source.needsReconnect ? 'Reconnect calendar first' : 'Sync now'}
                    >
                      <SyncIcon fontSize="small" sx={{ animation: syncingId === source.id ? 'spin 1s linear infinite' : undefined, '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDisconnect(source.id)}
                      disabled={disconnectingId === source.id}
                      title="Disconnect"
                      sx={{ color: 'error.main' }}
                    >
                      <DisconnectIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
